import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import UVHeroBackground from "@/components/visuals/UVHeroBackground";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/15657b70-52f9-4fed-be78-382892abee41/images/019314262e1795a79fc569426f91a28b74f05aceb2ddddc1d4392c61be835fd4.png";

const particles = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 1,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 8,
  duration: Math.random() * 12 + 12,
  glow: Math.random() > 0.7,
}));

export default function HeroSection() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isLight = theme === "light";
  return (
    <section id="hero" data-testid="hero-section" className="relative h-screen flex items-center overflow-hidden bg-[#020408]">
      {/* Light-mode custom background (UV beam + blue virus illustration) */}
      {isLight && <UVHeroBackground />}

      {/* Dark-mode original photographic background */}
      {!isLight && (
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-60" style={{ filter: "saturate(1.2) brightness(0.9)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(2,4,8,0.92) 0%, rgba(2,4,8,0.6) 40%, rgba(60,8,10,0.3) 70%, rgba(2,4,8,0.7) 100%)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020408] via-transparent to-[#020408]/40" />
        </div>
      )}

      {/* Volumetric blue glow orbs */}
      <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] rounded-full bg-[#EE1D25]/10 blur-[180px] animate-pulse" style={{ animationDuration: "6s" }} />
      <div className="absolute bottom-1/3 right-1/5 w-[300px] h-[300px] rounded-full bg-[#EE1D25]/8 blur-[120px] animate-pulse" style={{ animationDuration: "8s" }} />
      <div className="absolute top-1/2 left-1/4 w-[200px] h-[200px] rounded-full bg-[#EE1D25]/6 blur-[100px] animate-pulse" style={{ animationDuration: "10s" }} />

      {/* Particle field */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map(p => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: p.glow ? p.size + 2 : p.size,
              height: p.glow ? p.size + 2 : p.size,
              left: `${p.x}%`,
              top: `${p.y}%`,
              background: p.glow ? '#FF4444' : '#EE1D25',
              opacity: p.glow ? 0.6 : 0.3,
              boxShadow: p.glow ? '0 0 8px 2px rgba(238,29,37,0.4)' : 'none',
              animation: `float ${p.duration}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl"
        >
          {/* Label */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-10 px-4 py-2 border border-[#1F2937]/60 bg-[#0A0F1A]/60"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#EE1D25] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#7B8BA3] font-medium font-mono">
              {t("hero.badge")}
            </span>
          </motion.div>

          {/* Primary headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-6xl md:text-7xl lg:text-[6.5rem] font-black tracking-tighter leading-[0.9] mb-6"
            data-testid="hero-headline"
          >
            <span className="text-white">{t("hero.headlineLine1")}</span>
            <br />
            {t("hero.headlineLine2Pre") && <span className="text-white">{t("hero.headlineLine2Pre")}</span>}
            <span className="text-[#EE1D25]">{t("hero.headlineLine2Red")}</span>
          </motion.h1>

          {/* Secondary headline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="font-heading text-xl md:text-2xl text-[#8B9DC3] font-medium tracking-tight mb-6 max-w-2xl"
          >
            {t("hero.subtitle")}
          </motion.p>

          {/* Body */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="text-sm md:text-base text-[#5A6B83] leading-[1.8] max-w-xl mb-12"
          >
            {t("hero.body")}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-wrap gap-3"
          >
            <Button
              data-testid="hero-explore-btn"
              className="bg-[#EE1D25] hover:bg-[#FF3333] !text-white px-7 py-5 text-sm font-medium rounded-none transition-all duration-300 hover:shadow-[0_0_30px_rgba(238,29,37,0.3)]"
              onClick={() => document.getElementById("technology")?.scrollIntoView({ behavior: "smooth" })}
            >
              {t("hero.explore")} <ArrowRight className="ml-2" size={16} />
            </Button>
            <Button
              data-testid="hero-investor-btn"
              variant="outline"
              className="border-[#1F2937] text-[#C5D0E0] hover:bg-[#0A0F1A] hover:border-[#EE1D25]/40 px-7 py-5 text-sm font-medium rounded-none bg-transparent transition-all duration-300"
              onClick={() => document.getElementById("investment")?.scrollIntoView({ behavior: "smooth" })}
            >
              {t("hero.investor")}
            </Button>
            <Button
              data-testid="hero-contact-btn"
              variant="outline"
              className="border-[#1F2937] text-[#C5D0E0] hover:bg-[#0A0F1A] hover:border-[#EE1D25]/40 px-7 py-5 text-sm font-medium rounded-none bg-transparent transition-all duration-300"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            >
              {t("hero.contact")}
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#4B5563] font-mono">{t("hero.scroll")}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ChevronDown className="text-[#4B5563]" size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
}
